import { useState } from "react";
import { CreditCard, Crown, Loader2, X } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { PREMIUM_MONTHLY_PRICE, TRIAL_DAYS, celebratePremium } from "@/lib/premium";
import {
  TRIAL_CTA,
  TRIAL_CANCEL_NOTE,
  TRIAL_DISCLOSURE,
  TRIAL_HEADLINE,
  TRIAL_SUBHEAD,
  TRIAL_TERMS,
  APPLE_DISCLOSURE,
  startTrialWithPaymentMethod,
  trialChannelLabel,
} from "@/lib/trial";
import { isNativeStoreApp } from "@/lib/store-billing";
import { useAuth } from "@/hooks/useAuth";

interface Props {
  surface: string;
  onClose: () => void;
  /** Called after a store purchase activates Premium (web redirects instead). */
  onActivated?: () => void | Promise<void>;
}

/**
 * Pre-purchase trial summary. Shows exactly what is charged, when, and how to
 * cancel before handing off to Stripe Checkout or the platform purchase sheet.
 */
export function TrialConfirmDialog({ surface, onClose, onActivated }: Props) {
  const { refreshPremium } = useAuth();
  const [busy, setBusy] = useState(false);

  const start = async () => {
    setBusy(true);
    try {
      const result = await startTrialWithPaymentMethod(surface);
      if (!result.ok) {
        if (result.error === "cancelled") return;
        toast.error(
          result.error === "store_not_configured"
            ? "Subscriptions aren't available on this device yet."
            : "Couldn't open checkout. Please try again.",
        );
        return;
      }
      if (result.channel === "store") {
        void celebratePremium();
        toast.success(`Premium trial active — ${TRIAL_DAYS} free days!`);
        await refreshPremium();
        await onActivated?.();
        onClose();
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="modal-overlay fixed inset-0 z-[130] flex items-end justify-center bg-black/70 backdrop-blur-sm sm:items-center"
      role="dialog"
      aria-modal="true"
      aria-label="Confirm your Premium free trial"
    >
      <div className="modal-panel w-full max-w-sm rounded-3xl border border-rule bg-card shadow-2xl animate-in fade-in slide-in-from-bottom-4">
        <div className="relative bg-gradient-to-br from-neon-pink via-purple-600 to-neon-blue px-6 py-5 text-center text-white">
          <button
            onClick={onClose}
            aria-label="Close trial confirmation"
            className="absolute right-4 top-4 text-white/80 transition hover:text-white"
          >
            <X className="size-5" />
          </button>
          <Crown className="mx-auto size-7 text-neon-yellow" aria-hidden="true" />
          <h2 className="mt-2 font-[family-name:var(--font-display)] text-lg font-extrabold">
            Inquira Premium Monthly
          </h2>
          <p className="text-sm font-semibold text-white/90">
            {TRIAL_HEADLINE} · {TRIAL_SUBHEAD}
          </p>
        </div>

        <div className="space-y-4 p-5">
          <dl className="divide-y divide-rule rounded-xl border border-rule">
            <div className="flex items-center justify-between p-3 text-sm">
              <dt className="text-ink-muted">Due today</dt>
              <dd className="font-extrabold text-ink">$0 during your introductory trial</dd>
            </div>
            <div className="flex items-center justify-between p-3 text-sm">
              <dt className="text-ink-muted">Free trial</dt>
              <dd className="font-extrabold text-ink">{TRIAL_DAYS} days</dd>
            </div>
            <div className="flex items-center justify-between p-3 text-sm">
              <dt className="text-ink-muted">Then</dt>
              <dd className="font-extrabold text-ink">{PREMIUM_MONTHLY_PRICE}/month</dd>
            </div>
            <div className="flex items-center justify-between p-3 text-sm">
              <dt className="text-ink-muted">Renewal</dt>
              <dd className="text-right font-semibold text-ink">Auto-renews monthly until canceled</dd>
            </div>
            <div className="flex items-center justify-between p-3 text-sm">
              <dt className="text-ink-muted">Cancellation</dt>
              <dd className="text-right font-semibold text-ink">Cancel anytime</dd>
            </div>
          </dl>

          <p className="rounded-xl border border-neon-pink/40 bg-neon-yellow/15 p-3 text-sm font-bold text-ink">
            {TRIAL_CANCEL_NOTE}
          </p>

          <p className="flex items-start gap-2 rounded-xl bg-muted/70 p-3 text-xs leading-relaxed text-ink">
            <CreditCard className="mt-0.5 size-4 shrink-0 text-neon-pink" aria-hidden="true" />
            {isNativeStoreApp() ? APPLE_DISCLOSURE : TRIAL_TERMS}
          </p>

          <div className="space-y-2">
            <Button
              variant="scan"
              size="full"
              disabled={busy}
              onClick={() => void start()}
              className="font-[family-name:var(--font-display)]"
            >
              {busy ? <Loader2 className="size-5 animate-spin" /> : <Crown className="size-5" />}
              {busy ? "Opening…" : TRIAL_CTA}
            </Button>
            <p className="text-center text-xs font-semibold text-ink">{TRIAL_DISCLOSURE}</p>
            <p className="text-center text-[11px] text-ink-muted">
              {isNativeStoreApp()
                ? `Billed securely through ${trialChannelLabel()} — Inquira never sees your card details.`
                : `Secure checkout by ${trialChannelLabel()} — Inquira never stores your card details.`}
            </p>
            <button onClick={onClose} className="w-full py-2 text-sm font-semibold text-ink-muted underline">
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
